<template>

  <calendar-toolbar
    v-model="calendarDate"
    :calendar="calendarRef"
    class="mb-8"
    :disabled="loadingRates"
  >

    <v-btn
      prepend-icon="$edit"
      :disabled="!props.location || loadingRates"
      @click="openNonEventBaseModal()"
    >
      <span class="d-lg-none">{{ $t('titles.default_rates_abbr') }}</span>
      <span class="d-none d-lg-inline">{{ $t('actions.edit_default_rates') }}</span>
    </v-btn>

    <v-btn
      prepend-icon="$spotRate"
      class="ml-4"
      :disabled="!location || loadingRates"
      @click="openCreateNonEventExceptionModal()"
    >
      <span class="d-lg-none">{{ $t('titles.spot_rates_abbr') }}</span>
      <span class="d-none d-lg-inline">{{ $t('actions.manage_spot_rates') }}</span>
    </v-btn>

    <v-btn
      :text="$t('actions.reload')"
      prepend-icon="$reload"
      class="ml-4"
      @click="maybeLoadRates()"
      :loading="loadingRates"
      :disabled="!location || loadingRates"
    />

    <v-btn-toggle v-model="calendarType" mandatory class="ml-4">
      <v-btn value="day">
        <span class="d-lg-none">D</span>
        <span class="d-none d-lg-inline">{{ $t('titles.day') }}</span>
      </v-btn>
      <v-btn value="month">
        <span class="d-lg-none">{{ $t('titles.month_abbr') }}</span>
        <span class="d-none d-lg-inline">{{ $t('titles.month') }}</span>
      </v-btn>
    </v-btn-toggle>

  </calendar-toolbar>

  <calendar-with-sidebar>

    <normalized-calendar
      ref="calendar"
      first-day-of-week="1"
      v-model="calendarDate"
      :type="calendarType"
      :events="calendarItems"
      @click:event="openItemContextMenu"
      :loading="loadingRates"
      :event-overlap-mode="calendarType === 'day' ? 'column' : monthLayoutFn"
    >

      <template v-slot:event="{ event }">

        <non-event-base-bar v-if="event.type === 'non_event_base'" :item="event" :calendar-type="calendarType" @dblclick="openNonEventBaseModal(event)" />
        <non-event-exception-bar v-if="event.type === 'non_event_exception'" :calendar-type="calendarType" :item="event" @dblclick="openEditNonEventExceptionModal(event)" />
        <event-base-bar v-if="event.type === 'event_base'" :item="event" :calendar-type="calendarType" @dblclick="openCreateEventExceptionFromBaseModal(event)" />
        <event-event-bar v-if="event.type === 'event_event'" :item="event" :calendar-type="calendarType" @dblclick="openCreateEventExceptionFromEventModal(event)" />
        <event-exception-bar v-if="event.type === 'event_exception'" :item="event" :calendar-type="calendarType" @dblclick="openEditEventExceptionModal(event)" />

      </template>

    </normalized-calendar>

    <v-menu
      v-model="contextMenuOpen"
      :activator="contextMenuActivator"
      @after-leave="contextMenuActivator = undefined"
    >

      <template v-if="contextMenuItem">
        <non-event-base-popup v-if="contextMenuItem.type === 'non_event_base'" :item="contextMenuItem as NonEventBaseItem" @edit="openNonEventBaseModal(contextMenuItem as NonEventBaseItem)" @add-exception="openCreateNonEventExceptionModal(contextMenuItem as NonEventBaseItem)" />
        <non-event-exception-popup v-if="contextMenuItem.type === 'non_event_exception'" :item="contextMenuItem as NonEventExceptionItem" @edit="openEditNonEventExceptionModal(contextMenuItem as NonEventExceptionItem)" @delete="openDeleteNonEventExceptionModal(contextMenuItem as NonEventExceptionItem)" />
        <event-base-popup v-if="contextMenuItem.type === 'event_base'" :item="contextMenuItem as EventBaseItem" />
        <event-event-popup v-if="contextMenuItem.type === 'event_event'" :item="contextMenuItem as EventEventItem" @add-exception="openCreateEventExceptionFromEventModal(contextMenuItem as EventEventItem)" />
        <event-exception-popup v-if="contextMenuItem.type === 'event_exception'" :item="contextMenuItem as EventExceptionItem" @edit="openEditEventExceptionModal(contextMenuItem as EventExceptionItem)" @delete="openDeleteEventExceptionModal(contextMenuItem as EventExceptionItem)" />
      </template>

    </v-menu>

    <template v-slot:sidebar>

      <div class="d-flex d-lg-block">

        <sidebar-datepicker
          v-model="calendarDate"
          class="mr-8 mr-lg-0 mb-lg-8"
        />

        <product-filter
          v-model="selectedLocationProductIds"
          :products="productFilterOptions"
          :loading="loadingLocationProducts"
        />

      </div>

    </template>

  </calendar-with-sidebar>

</template>

<script setup lang="ts">

import { computed, ref, toRef, useTemplateRef, watch } from 'vue'
import { useLocationData } from './useLocationData'

// components
import CalendarToolbar from '../../../CalendarToolbar.vue'
import CalendarWithSidebar from '../../../CalendarWithSidebar.vue'
import NormalizedCalendar from '../../../NormalizedCalendar.vue'
import SidebarDatepicker from '../../../SidebarDatepicker.vue'
import ProductFilter from '../../../ProductFilter.vue'
// @ts-ignore
import DefaultRatesModal from './modals/DefaultRatesModal.vue'
import ManageSpotRatesOrEventTiersModal from './modals/ManageSpotRatesOrEventTiersModal.vue'
import DeleteSpotRatesModal from './modals/DeleteSpotRatesModal.vue'
import DeleteEventTiersModal from './modals/DeleteEventTiersModal.vue'

import NonEventBaseBar from './bars/NonEventBaseBar.vue'
import NonEventExceptionBar from './bars/NonEventExceptionBar.vue'
import EventBaseBar from './bars/EventBaseBar.vue'
import EventEventBar from './bars/EventEventBar.vue'
import EventExceptionBar from './bars/EventExceptionBar.vue'

import NonEventBasePopup from './popups/NonEventBasePopup.vue'
import NonEventExceptionPopup from './popups/NonEventExceptionPopup.vue'
import EventBasePopup from './popups/EventBasePopup.vue'
import EventEventPopup from './popups/EventEventPopup.vue'
import EventExceptionPopup from './popups/EventExceptionPopup.vue'

// models
import { SpotRate } from '../../../../models/SpotRate'
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'

// misc
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import { Temporal } from '@js-temporal/polyfill'
import type {
  AbstractRateCalendarItem,
  NonEventBaseItem,
  NonEventExceptionItem,
  EventBaseItem,
  EventEventItem,
  EventExceptionItem
} from './types'
import { range } from 'underscore'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import type { DateRange } from '../../../../types/misc'

/**
 * Props
 */

interface Props {
  location: null | Location // TODO: note which relations (if any) are assumed to be loaded
}

const props = defineProps<Props>()

/**
 * Calendar component
 */

const calendarRef = useTemplateRef<NormalizedCalendarType>('calendar')
const calendarDate = ref<Temporal.PlainDate>(Temporal.Now.plainDateISO())
const calendarType = ref<'day' | 'month'>('month')

// calendarStart and calendarEnd indicate the earliest and latest dates that are currently displayed across all calendar types

const calendarStart = computed<null | Temporal.PlainDate>(() => {
  return calendarRef.value?.monthStart ?? null
})

const calendarEnd = computed<null | Temporal.PlainDate>(() => {
 return calendarRef.value?.monthEnd ?? null
})

const monthDates = computed<Temporal.PlainDate[]>(() => {
  const start = calendarRef.value?.monthStart
  const end = calendarRef.value?.monthEnd
  if (!start || !end) {
    return []
  }
  const numDates = start.until(end).days + 1
  return range(0, numDates).map(i => start.add({days: i}))
})

/**
 * Composables and such
 */

type NormalizedCalendarType = InstanceType<typeof NormalizedCalendar>
const { locationProducts, loadingLocationProducts, loadRates, loadingRates, defaultRatesByLocationProductId, inventoriesWithEventTiers, rawEvents } = useLocationData(toRef(() => props.location), calendarStart, calendarEnd)

function maybeLoadRates() {
  if (props.location) {
    contextMenuItem.value = null
    contextMenuActivator.value = undefined
    loadRates(props.location.ID)
  }
}

/**
 * Items which are currently displayed on the calendar.
 */
const calendarItems = computed<AbstractRateCalendarItem[]>(() => {

  if (calendarType.value === 'month') {
    return monthItemsForSelectedLps.value
  } else if (calendarType.value === 'day') {
    return dayItemsForSelectedLps.value
  } else {
    return []
  }

})

const locationProductsWithSyncedCdrs = computed<LocationProduct[]>(() => {
  return locationProducts.value.filter(lp => {
    if (lp.product?.verticalType === 'event') {
      return true
    }
    const drs = defaultRatesByLocationProductId.value[lp.ID] ?? []
    return drs.some(dr => dr.channelDefaultRates.length > 0)
  })
})

const productFilterOptions = computed(() => {
  return locationProductsWithSyncedCdrs.value.map(lp => {
    return {
      id: lp.ID,
      name: lp.product?.name ?? 'unknown',
      vertical: lp.product.verticalType,
    }
  })
})

/**
 * All items (except for EventEventItems) for the "Month" mode of the calendar.
 */
const monthItems = computed<{[key: number]: AbstractRateCalendarItem[]}>(() => {

  const output: {[key: number]: AbstractRateCalendarItem[]} = {}

  locationProductsWithSyncedCdrs.value.forEach(lp => {

    let lpItems

    if (lp.product.verticalType === 'event') {
      lpItems = buildMonthItemsForEventLp(lp)
    } else {
      lpItems = buildMonthItemsForNonEventLp(lp)
    }

    output[lp.ID] = lpItems

  })

  return output

})

function buildMonthItemsForNonEventLp(lp: LocationProduct): AbstractRateCalendarItem[] {

  const output: AbstractRateCalendarItem[] = []

  const vertical = VerticalRegistry.get(lp.product.verticalType)

  if (!vertical) {
    console.error(`couldn't find vertical for ${lp.ID}`)
    return []
  }

  const defaultRates = defaultRatesByLocationProductId.value[lp.ID] ?? []

  if (!defaultRates) {
    return []
  }

  // build items for spot rates

  const spotRatesByDateRange: {[key: string]: SpotRate[]} = {}

  defaultRates.forEach(dr => {
    dr.spotRates.forEach(sr => {
      const key = `${sr.startDate}_${sr.endDate}`
      if (!(key in spotRatesByDateRange)) {
        spotRatesByDateRange[key] = []
      }
      spotRatesByDateRange[key].push(sr)
    })
  })

  const datesWithExceptions = new Set<string>()

  Object.values(spotRatesByDateRange).forEach(spotRates => {

    const event: NonEventExceptionItem = {
      type: 'non_event_exception',
      locationProduct: lp,
      defaultRates: defaultRates,
      spotRates: spotRates,
      name: lp.product.name, // for fallback purposes only
      start: spotRates[0].startDate,
      end: spotRates[0].endDate,
      color: vertical?.color,
      uuid: Math.random(), // TODO: remove
    }

    output.push(event)

    const psrStart = Temporal.PlainDate.from(spotRates[0].startDate)
    const psrEnd = Temporal.PlainDate.from(spotRates[0].endDate)
    const numPsrDates = psrStart.until(psrEnd).days + 1
    range(0, numPsrDates).forEach(i => {
      datesWithExceptions.add(psrStart.add({days: i}).toString())
    })

  })

  // build placeholders (if applicable)

  const datesWithPlaceholders = new Set<string>()

  if (vertical.supports(['limitedByWeekday'])) {

    monthDates.value.forEach(date => {

      const dayOfWeek = date.dayOfWeek === 7 ? 0 : date.dayOfWeek
      const isoDate = date.toString()

      if (datesWithExceptions.has(isoDate)) {
        return
      }

      if (!lp.product.enabledWeekdays.includes(dayOfWeek)) {
        datesWithPlaceholders.add(isoDate)
      }
    })

  }

  // build default rate items to fill whatever dates are left

  let pendingEvent: null | NonEventBaseItem = null

  monthDates.value.forEach(date => {

    const isoDate = date.toString()
    const isDrDate = !datesWithExceptions.has(isoDate) && !datesWithPlaceholders.has(isoDate)

    if (isDrDate) {
      if (pendingEvent) {
        // extend the pendingEvent to include this date
        pendingEvent.end = isoDate
      } else {
        // this is the start of a DR
        pendingEvent = {
          type: 'non_event_base',
          locationProduct: lp,
          defaultRates: defaultRates,
          start: isoDate,
          end: isoDate,
          color: vertical.color,
        }
      }
    } else {
      if (pendingEvent) {
        output.push(pendingEvent)
        pendingEvent = null
      }
    }

  })

  // tie up loose ends
  if (pendingEvent) {
    output.push(pendingEvent)
    pendingEvent = null
  }

  return output

}

const eventVertical = VerticalRegistry.get('event')

function buildMonthItemsForEventLp(lp: LocationProduct): AbstractRateCalendarItem[] {

  const output: AbstractRateCalendarItem[] = []

  // base item
  const baseItem: EventBaseItem = {
    type: 'event_base',
    locationProduct: lp,
    start: calendarRef.value?.monthStart?.toString() ?? '2000-01-01',
    end: calendarRef.value?.monthEnd?.toString() ?? '2050-01-01',
    color: eventVertical?.color,
  }

  output.push(baseItem)

  // exceptions and events

  const lpVenueIds = lp.venueLocationProducts.map(vlp => vlp.venueID)
  const lpEvents = rawEvents.value.filter(e => lpVenueIds.includes(e.venueID))

  lpEvents.forEach(event => {

    const itemDate = Temporal.PlainDateTime.from(event.eventStartLocal).toPlainDate()
    const vlp = lp.venueLocationProducts.find(vlp => vlp.venueID === event.venueID) ?? null

    const existingException = inventoriesWithEventTiers.value.find(iwet => {
      return iwet.locationProductID === lp.ID && iwet.eventID === event.ID
    })

    if (existingException) {

      const item: EventExceptionItem = {
        type: 'event_exception',
        locationProduct: lp,
        inventory: existingException,
        start: itemDate.toString(),
        end: itemDate.toString(),
        color: 'transparent',
      }

      output.push(item)

    } else {

      const item: EventEventItem = {
        type: 'event_event',
        locationProduct: lp,
        venueLocationProduct: vlp,
        name: event.name,
        event: event,
        start: itemDate.toString(),
        end: itemDate.toString(),
        color: 'transparent',
      }

      output.push(item)

    }

  })

  return output

}

/**
 * Filtered items for the "Month" mode.
 */
const monthItemsForSelectedLps = computed<AbstractRateCalendarItem[]>(() => {
  const output: AbstractRateCalendarItem[] = []
  selectedLocationProductIds.value.forEach(lpId => {
    if (lpId in monthItems.value) {
      output.push(...monthItems.value[lpId])
    }
  })
  return output
})

const midnight = new Temporal.PlainTime(0, 0, 0)

/**
 * All items for the "Day" mode.
 */
const dayItems = computed<{[key: number]: AbstractRateCalendarItem[]}>(() => {

  const output: {[key: number]: AbstractRateCalendarItem[]} = {}

  selectedLocationProducts.value.forEach(lp => {

    let lpItems

    if (lp.product.verticalType === 'event') {
      lpItems = buildDayItemsForEventLp(lp)
    } else {
      lpItems = buildDayItemsForNonEventLp(lp)
    }

    output[lp.ID] = lpItems

  })

  return output

})

function buildDayItemsForNonEventLp(lp: LocationProduct): AbstractRateCalendarItem[] {

  const output: AbstractRateCalendarItem[] = []

  const vertical = VerticalRegistry.get(lp.product.verticalType)
  if (!vertical) {
    console.warn('skipping because no vertical:', lp)
    return []
  }

  const defaultRates = defaultRatesByLocationProductId.value[lp.ID] ?? []

  // find any spotRates which overlap with the displayed date

  const spotRates: SpotRate[] = []
  const isoCalendarDate = calendarDate.value.toString()

  defaultRates.forEach(dr => {
    dr.spotRates.forEach(sr => {
      if (sr.startDate <= isoCalendarDate && sr.endDate >= isoCalendarDate) {
        spotRates.push(sr)
      }
    })
  })

  // determine the effective start and end times for this LP
  // (represented as hours after midnight)

  let itemStartTime: Temporal.Duration = Temporal.Duration.from({hours: 0})
  let itemEndTime = Temporal.Duration.from({hours: 24})

  if (vertical.supports(['startAndEndTimes'])) {
    const isoProductStart = lp.firstStartTime ?? lp.product.firstStartTime ?? null
    if (isoProductStart) {
      const productStart = Temporal.PlainTime.from(isoProductStart)
      itemStartTime = Temporal.Duration.from({
        hours: productStart.hour,
        minutes: productStart.minute,
        seconds: productStart.second,
      })
    }
    const isoProductEnd = lp.endTime ?? lp.product.endTime ?? null
    if (isoProductEnd) {
      const productEnd = Temporal.PlainTime.from(isoProductEnd)
      const productEndsAtMidnight = Temporal.PlainTime.compare(productEnd, midnight) === 0
      if (!productEndsAtMidnight) {
        itemEndTime = Temporal.Duration.from({
          hours: productEnd.hour,
          minutes: productEnd.minute,
          seconds: productEnd.second,
        })
      }
    }
  }

  const midnightToday = calendarDate.value.toPlainDateTime({hour: 0, minute: 0, second: 0})
  const itemStart = midnightToday.add(itemStartTime)
  const itemEnd = midnightToday.add(itemEndTime)

  if (spotRates.length) {

    const item: NonEventExceptionItem = {
      type: 'non_event_exception',
      locationProduct: lp,
      defaultRates: defaultRates,
      spotRates: spotRates,
      name: lp.product.name, // for fallback purposes only
      start: itemStart.toString(),
      end: itemEnd.toString(),
      color: vertical?.color,
    }

    output.push(item)

  } else {

    const verticalLimitedByWeekday = vertical.supports(['limitedByWeekday'])
    const dayOfWeek = calendarDate.value.dayOfWeek === 7 ? 0 : calendarDate.value.dayOfWeek
    const lpActiveToday = !verticalLimitedByWeekday || lp.product.enabledWeekdays.includes(dayOfWeek)

    if (lpActiveToday) {

      const item: NonEventBaseItem = {
        type: 'non_event_base',
        locationProduct: lp,
        defaultRates: defaultRates,
        name: lp.product.name, // for fallback purposes only
        start: itemStart.toString(),
        end: itemEnd.toString(),
        color: vertical?.color,
      }

      output.push(item)

    }

  }

  return output

}

function buildDayItemsForEventLp(lp: LocationProduct): AbstractRateCalendarItem[] {

  const output: AbstractRateCalendarItem[] = []

  // base item

  const baseStart = calendarDate.value.toPlainDateTime({hour: 0, minute: 0, second: 0})
  const baseEnd = baseStart.add({days: 1})

  const baseItem: EventBaseItem = {
    type: 'event_base',
    locationProduct: lp,
    start: baseStart.toString(),
    end: baseEnd.toString(),
    color: eventVertical?.color,
  }

  output.push(baseItem)

  // exceptions and events

  const lpVenueIds = lp.venueLocationProducts.map(vlp => vlp.venueID)
  const lpEvents = rawEvents.value.filter(e => {
    return (
      lpVenueIds.includes(e.venueID)
      // TODO: also filter by date!
    )
  })

  lpEvents.forEach(event => {

    const vlp = lp.venueLocationProducts.find(vlp => vlp.venueID === event.venueID) ?? null

    const itemStart = Temporal.PlainDateTime.from(event.eventStartLocal)
    const itemEnd = Temporal.PlainDateTime.from(event.eventEndLocal)

    const existingException = inventoriesWithEventTiers.value.find(iwet => {
      return iwet.locationProductID === lp.ID && iwet.eventID === event.ID
    })

    if (existingException) {

      const item: EventExceptionItem = {
        type: 'event_exception',
        locationProduct: lp,
        // venueLocationProduct: vlp,
        inventory: existingException,
        start: itemStart.toString(),
        end: itemEnd.toString(),
        color: 'transparent',
      }

      output.push(item)

    } else {

      const item: EventEventItem = {
        type: 'event_event',
        locationProduct: lp,
        venueLocationProduct: vlp,
        name: event.name,
        event: event,
        start: itemStart.toString(),
        end: itemEnd.toString(),
        color: 'transparent',
      }

      output.push(item)

    }

  })

  return output

}

/**
 * Filtered items for the "Day" mode.
 */
const dayItemsForSelectedLps = computed<AbstractRateCalendarItem[]>(() => {
  const output: AbstractRateCalendarItem[] = []
  selectedLocationProductIds.value.forEach(lpId => {
    if (lpId in dayItems.value) {
      output.push(...dayItems.value[lpId])
    }
  })
  return output
})


/**
 *
 */
const selectedLocationProducts = computed<LocationProduct[]>(() => {
  return locationProducts.value.filter(lp => selectedLocationProductIds.value.includes(lp.ID))
})

/**
 *
 */
const selectedLpOrderMap = computed<{[key: number]: number}>(() => {

  const output: {[key: number]: number} = {}
  let order = 0

  selectedLocationProducts.value.forEach(locationProduct => {
    output[locationProduct.ID] = order
    order++
  })

  return output

})

/**
 * When the list of LPs changes, enable all of the new LPs in the filters.
 */
watch(locationProducts, () => {
  selectedLocationProductIds.value = locationProducts.value.map(lp => lp.ID)
})

const selectedLocationProductIds = ref<number[]>([])

/**
 * Context menu
 * Based on: https://vuetifyjs.com/en/components/calendars/#click
 */

const contextMenuOpen = ref<boolean>(false)
const contextMenuItem = ref<null | AbstractRateCalendarItem>(null)
const contextMenuActivator = ref<undefined | HTMLElement>()

function openItemContextMenu(nativeEvent: Event, { event }: {event: AbstractRateCalendarItem}) {

  if (!nativeEvent.target || !(nativeEvent.target instanceof HTMLElement)) {
    // we can't do anything with this (this shouldn't happen anyway)
    return
  }

  const open = () => {
    contextMenuItem.value = event
    contextMenuActivator.value = nativeEvent.target as HTMLElement
    requestAnimationFrame(() => requestAnimationFrame(() => contextMenuOpen.value = true))
  }

  if (contextMenuOpen.value) {
    contextMenuOpen.value = false
    requestAnimationFrame(() => requestAnimationFrame(() => open()))
  } else {
    open()
  }

  nativeEvent.stopPropagation()

}

/**
 * Modals
 */

function openNonEventBaseModal(item: null | NonEventBaseItem = null) {

  openDialog(DefaultRatesModal, {
    location: props.location,
    locationProductID: item?.locationProduct.ID ?? null,
  }).then((ratesChanged: boolean) => {
    if (ratesChanged) {
      maybeLoadRates()
    }
  })

}

function openCreateNonEventExceptionModal(item: null | NonEventBaseItem = null) {
  openExceptionModal(item?.locationProduct.ID, item?.locationProduct.product?.verticalType ?? null)
}

function openEditNonEventExceptionModal(item: NonEventExceptionItem) {
  const dateRange = {start: Temporal.PlainDate.from(item.start), end: Temporal.PlainDate.from(item.end)}
  openExceptionModal(item.locationProduct.ID, null, dateRange)
}

function openDeleteNonEventExceptionModal(item: NonEventExceptionItem) {

  openDialog(DeleteSpotRatesModal, {
    item: item,
  }).then((exceptionDeleted: boolean) => {
    if (exceptionDeleted) {
      maybeLoadRates()
    }
  })

}

function openEditEventExceptionModal(item: EventExceptionItem) {

  const eventDate = Temporal.PlainDate.from(item.inventory.date)
  const dateRange = {start: eventDate, end: eventDate}
  openExceptionModal(item.locationProduct.ID, 'event', dateRange)

}

function openDeleteEventExceptionModal(item: EventExceptionItem) {

  openDialog(DeleteEventTiersModal, {
    inventoryId: item.inventory.ID,
  }).then((exceptionDeleted: boolean) => {
    if (exceptionDeleted) {
      maybeLoadRates()
    }
  })

}

function openExceptionModal(
  locationProductId: null | number = null,
  vertical: null | string = null,
  dateRange: null | DateRange = null,
) {

  openDialog(ManageSpotRatesOrEventTiersModal, {
    location: props.location,
    locationProductId: locationProductId,
    verticalFilter: vertical,
    dateRange: dateRange,
  }).then((ratesChanged: boolean) => {
    if (ratesChanged) {
      maybeLoadRates()
    }
  })

}

function openCreateEventExceptionFromBaseModal(item: EventBaseItem) {
  openExceptionModal(item.locationProduct.ID, 'event')
}

function openCreateEventExceptionFromEventModal(item: EventEventItem) {

  // autoselect the first event LP which is linked to this venue
  const locationProduct = selectedLocationProducts.value.find(lp => {
    return (
      lp.product?.verticalType === 'event' &&
      lp.venueLocationProducts.some(vlp => vlp.venueID === item.event.venueID)
    )
  })

  // autoselect the event's start date
  const eventDate = Temporal.PlainDateTime.from(item.start).toPlainDate()
  const dateRange: DateRange = {start: eventDate, end: eventDate}

  openExceptionModal(locationProduct ? locationProduct.ID : null, 'event', dateRange)

}

// import type {
//   CalendarDaySlotScope,
//   CalendarEventParsed,
//   CalendarEventVisual
// } from 'vuetify/lib/components/VCalendar/types'

function monthLayoutFn(
  _events: any[], // CalendarEventParsed[],
  _firstWeekday: number,
  _overlapThreshold: number,
): Function {

  return (
    _day: any, // CalendarDaySlotScope,
    dayEvents: any[], // CalendarEventParsed[],
    _timed: boolean,
    _reset: boolean,
  ): any[] /* CalendarEventVisual[] */ => {

    // note: for the "month" calendar type, the term "column" actually refers to vertical positioning (not horizontal)

    const output: any[] /* CalendarEventVisual[] */ = []
    const normalItems = dayEvents.filter(e => !['event_event', 'event_exception'].includes(e.input.type))
    const eventExceptionItems = dayEvents.filter(e => e.input.type === 'event_exception')
    const eventEventItems = dayEvents.filter(e => e.input.type === 'event_event')

    const numLps = selectedLocationProductIds.value.length
    const columnCount = numLps + eventExceptionItems.length + eventEventItems.length

    normalItems.forEach(event => {

      const column = selectedLpOrderMap.value[event.input.locationProduct.ID] ?? 999

      output.push({
        event: event,
        columnCount: columnCount, // not certain that I'm using this correctly, or whether it's necessary
        column: column,
        left: 0, // I think this only applies to the day-based calendarTypes
        width: 0, // I think this only applies to the day-based calendarTypes
      })

    })

    let column = numLps

    eventExceptionItems.forEach(event => {

      output.push({
        event: event,
        columnCount: columnCount,
        column: column,
        left: 0,
        width: 0,
      })

      column++

    })

    eventEventItems.forEach(event => {

      output.push({
        event: event,
        columnCount: columnCount,
        column: column,
        left: 0,
        width: 0,
      })

      column++

    })

    return output

  }

}

</script>

<style lang="scss" >

@use '../../../../styles/settings';

// put a gutter between columns in the "Day" view
// this isn't perfect, but VCalendar doesn't seem to expose anything else we can use
.v-calendar-daily {
  .v-event-timed {
    border-right: 5px settings.$application-background solid;
  }
}

</style>
